import React, { useState, useEffect } from 'react';

import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { pipelineService } from '../../services/pipelineService';
import { projectService } from '../../services/projectService';
import NavigationBar from '../../components/ui/NavigationBar';
import BreadcrumbTrail from '../../components/ui/BreadcrumbTrail';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import ProjectSelector from './components/ProjectSelector';
import PipelineBuilder from './components/PipelineBuilder';
import StageConfigPanel from './components/StageConfigPanel';
import TemplateLibrary from './components/TemplateLibrary';
import PipelineAnalytics from './components/PipelineAnalytics';

const DynamicPipelineConfigurationCenter = () => {
  const navigate = useNavigate();
  const { user, userProfile, isAdmin } = useAuth();
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState(null);
  const [activeTab, setActiveTab] = useState('builder');

  const breadcrumbItems = [
    { label: 'Inicio', path: '/', icon: 'Home' },
    { label: 'Admin Dashboard', path: '/admin-dashboard', icon: 'Shield' },
    { label: 'Pipeline Configuration', path: '/dynamic-pipeline-configuration-center', icon: 'GitBranch' }
  ];

  useEffect(() => {
    loadProjectAndPipeline();
  }, []);

  const loadProjectAndPipeline = async () => {
    try {
      setLoading(true);
      // Load Residencial 3 Volcanes project
      const projectData = await projectService?.getProjects();
      if (projectData && projectData?.length > 0) {
        const project = projectData?.[0];
        setSelectedProject(project);
        await loadPipelineStages(project?.id);
      }
    } catch (error) {
      console.error('Error loading project:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (selectedProject?.id) {
      loadPipelineStages(selectedProject?.id);
    }
  }, [selectedProject]);

  const loadProjects = async () => {
    try {
      setLoading(true);
      const projectsData = await pipelineService?.getProjects();
      setProjects(projectsData || []);
      if (projectsData?.length > 0) {
        setSelectedProject(projectsData?.[0]);
      }
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadPipelineStages = async (projectId) => {
    try {
      const stagesData = await pipelineService?.getPipelineStages(projectId);
      setStages(stagesData || []);
    } catch (error) {
      console.error('Error loading pipeline stages:', error);
    }
  };

  const handleStageCreate = async (stageData) => {
    try {
      await pipelineService?.createPipelineStage({
        ...stageData,
        projectId: selectedProject?.id
      });
      await loadPipelineStages(selectedProject?.id);
    } catch (error) {
      console.error('Error creating stage:', error);
    }
  };

  const handleStageUpdate = async (stageId, updates) => {
    try {
      await pipelineService?.updatePipelineStage(stageId, updates);
      await loadPipelineStages(selectedProject?.id);
    } catch (error) {
      console.error('Error updating stage:', error);
    }
  };

  const handleStageDelete = async (stageId) => {
    try {
      await pipelineService?.deletePipelineStage(stageId);
      await loadPipelineStages(selectedProject?.id);
      setSelectedStage(null);
    } catch (error) {
      console.error('Error deleting stage:', error);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <NavigationBar />
        <div className="flex items-center justify-center h-[calc(100vh-80px)]">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Cargando configuración de pipeline...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <NavigationBar />

      <div className="container mx-auto px-4 py-6 sm:py-8 mt-16 sm:mt-20">
        <BreadcrumbTrail items={breadcrumbItems} />

        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-foreground mb-2 flex items-center">
                <Icon name="GitBranch" size={32} className="mr-3 text-primary" />
                Dynamic Pipeline Configuration Center
              </h1>
              <p className="text-sm sm:text-base text-muted-foreground">
                Configure custom pipeline stages, workflows, and automation rules per project
              </p>
            </div>
            <Button
              iconName="ArrowLeft"
              variant="outline"
              onClick={() => navigate('/admin-dashboard')}
            >
              Back to Admin
            </Button>
          </div>
        </div>

        {/* Project Selector */}
        <div className="mb-6">
          <ProjectSelector
            projects={projects}
            selectedProject={selectedProject}
            onSelectProject={setSelectedProject}
            onRefresh={loadProjects}
          />
        </div>

        {/* Tab Navigation */}
        <div className="flex space-x-2 mb-6 border-b border-border overflow-x-auto">
          <button
            onClick={() => setActiveTab('builder')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'builder' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Workflow" size={18} className="inline mr-2" />
            Pipeline Builder
          </button>
          <button
            onClick={() => setActiveTab('analytics')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'analytics' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="BarChart3" size={18} className="inline mr-2" />
            Analytics
          </button>
          <button
            onClick={() => setActiveTab('templates')}
            className={`px-4 py-2 font-medium transition-colors whitespace-nowrap ${
              activeTab === 'templates' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
            }`}
          >
            <Icon name="Library" size={18} className="inline mr-2" />
            Templates
          </button>
        </div>

        {/* Tab Content */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content Area */}
          <div className="lg:col-span-2">
            {activeTab === 'builder' && (
              <PipelineBuilder
                stages={stages}
                onStageCreate={handleStageCreate}
                onStageSelect={setSelectedStage}
                onStageReorder={loadPipelineStages}
              />
            )}
            {activeTab === 'analytics' && (
              <PipelineAnalytics projectId={selectedProject?.id} stages={stages} />
            )}
            {activeTab === 'templates' && (
              <TemplateLibrary onApplyTemplate={loadPipelineStages} projectId={selectedProject?.id} />
            )}
          </div>

          {/* Side Panel */}
          <div className="lg:col-span-1">
            {selectedStage && activeTab === 'builder' && (
              <StageConfigPanel
                stage={selectedStage}
                onUpdate={handleStageUpdate}
                onDelete={handleStageDelete}
                onClose={() => setSelectedStage(null)}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DynamicPipelineConfigurationCenter;